/**
 * OpenSeadragon viewer defaults.
 *
 * These are code defaults only. Organisation `tuning` and per-user `sensitivity`
 * overrides (see docs/0006_osd-viewer-port.md, Settings model) are merged on top.
 */
export const OSD_CONFIG = {
    // Navigator (minimap). Position comes from styles.css; see NAVIGATOR_LAYOUT.
    showNavigator: true,
    navigatorSizeRatio: 0.18,
    // false so a user resize via setWidth/Height is not overwritten on viewer resize
    navigatorMaintainSizeRatio: false,
    navigatorAutoResize: false,
    navigatorAutoFade: false,
    navigatorResize: {
        storageKey: 'gamma-osd-navigator-size',
        minSize: 80,
        maxRatio: 0.45,
    },

    // Built-in OSD controls are replaced by the gamma viewer's own React controls.
    showNavigationControl: false,
    showZoomControl: false,
    showHomeControl: false,
    showFullPageControl: false,

    // Spring used by animated zooms (buttons, double-click); the wheel has its own ease.
    animationTime: 0.55,
    springStiffness: 5.5,

    zoomPerScroll: 1.05,
    zoomPerSecond: 2.0,
    zoomPerClick: 2.0,
    minScrollDeltaTime: 0,

    // Custom continuous wheel (BaseSlideMapOSD). Fine = trackpad; coarse = mouse notches.
    pixelsPerWheelLine: 40,
    pixelsPerArrowPress: 40,
    wheelCoarseThreshold: 40,
    wheelZoomRate: 0.216,
    wheelZoomRateCoarse: 0.085,
    wheelZoomMaxScalePerFrame: 1.62,
    wheelZoomMaxScalePerFrameCoarse: 1.22,
    wheelZoomSmoothing: 0.22,
    wheelZoomSettleEpsilon: 1e-4,

    // Tile loading: scale already-loaded tiles while sharper levels arrive.
    immediateRender: true,
    // Prefer pyramid levels finer than ideal (OSD default 0.5).
    minPixelRatio: 0.2,
    preferFinerMorphleLevels: 2,
    maxImageCacheCount: 2000,
    imageLoaderLimit: 32,
    prefetchNeighborLevelOffsets: [1, 2, -1, -2],
    prefetchMaxTilesPerTick: 24,
    prefetchFinerMaxTilesPerTick: 32,
    timeout: 30000,
    preload: true,

    // The real zoom limits are set on the viewport in applyOsdZoomBounds.
    maxZoomPixelRatio: 2.0,
    minZoomImageRatio: 0.8,

    blendTime: 0,
    alwaysBlend: false,
    smoothTileEdgesMinZoom: Infinity,
    subPixelRoundingForTransparency: false,
    placeholderFillStyle: '#f0f0f0',
    opacity: 1.0,
    useCanvas: true,
    autoResize: true,

    loadTilesWithAjax: false,
    crossOriginPolicy: 'Anonymous',
    wrapHorizontal: false,
    wrapVertical: false,
    preserveViewport: true,

    gestureSettingsMouse: {
        // Wheel zoom and Ctrl+wheel rotate are handled in BaseSlideMapOSD.
        scrollToZoom: false,
        clickToZoom: false,
        dblClickToZoom: false,
        dblClickDragToZoom: false,
        pinchToZoom: true,
        flickEnabled: true,
        flickMinSpeed: 200,
        flickMomentum: 0.1,
        flickMomentumDrag: 0.92,
    },
    gestureSettingsTouch: {
        scrollToZoom: false,
        clickToZoom: false,
        dblClickToZoom: false,
        dblClickDragToZoom: false,
        pinchToZoom: true,
        // Rotation is part of the unified canvas-pinch handler in BaseSlideMapOSD.
        pinchRotate: false,
        flickEnabled: true,
        flickMinSpeed: 200,
        flickMomentum: 0.12,
        flickMomentumDrag: 0.9,
    },
    gestureSettingsPen: {
        scrollToZoom: false,
        clickToZoom: false,
        dblClickToZoom: false,
        pinchToZoom: false,
        flickEnabled: true,
        flickMinSpeed: 200,
        flickMomentum: 0.1,
        flickMomentumDrag: 0.92,
    },

    backgroundColor: '#dcdce6',
    debugMode: false,
};

/** Must match `.openseadragon-container .navigator` and the toggle button in styles.css / BaseSlideMapOSD. */
export const NAVIGATOR_LAYOUT = {
    top: 60,
    right: 20,
    gripSize: 14,
    toggleTop: 20,
    toggleRight: 20,
};

/** Rotation settles over several frames and field images decode late; redraw the overlay at these delays. */
export const ROTATE_OVERLAY_RETRY_MS = [16, 48, 120, 280];

/** Extra navigator grip re-measures after the minimap mounts. */
export const NAVIGATOR_SYNC_DELAYS_MS = [100, 400];

/**
 * Camera-field images kept by SlidemapOSD. Fields of the shown tiled level and its neighbours stay
 * cached for zooming back; older off-screen fields are dropped past the per-level limit.
 */
export const FIELD_IMAGE_CACHE = {
    neighbourLevels: 1,
    maxImagesPerLevel: 300,
};

/** Zoom factor for one zoom button press or double-click. */
export const CLICK_ZOOM_FACTOR = 2;

export const DEFAULT_MICRONS_PER_PIXEL = 0.2155;

export const SCALE_BAR = {
    niceValuesMicrons: [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000],
    minWidthPx: 30,
    preferredMaxWidthPx: 150,
    maxWidthPx: 200,
};
